import { useUnitItemContext } from "@/hooks/useUnitItemContext";
import { ButtonLayout, Step } from "@/types/enums";
import { useDisplayOptionContext } from "@/hooks/useDisplayOptionContext";
import {
    selectCurrentStep,
} from "@/features/journey/journeySlice.ts";
import { useSelector } from "react-redux";

/**
 * Determines if the rent/reserve step should be shown based on
 * subgroup count and button layout configuration
 */
export const shouldShowRentReserveStep = (
    subGroupCount: number,
    buttonLayout: ButtonLayout | undefined,
): boolean => {
    if (buttonLayout === ButtonLayout.COMBINED) return true;
    return subGroupCount <= 1;
};

/**
 * Returns an array of the steps needed for the rental journey
 * This is a pure function that can be used both in hooks and in Redux actions
 */
export const getSteps = (
    subGroupCount: number,
    buttonLayout: ButtonLayout | undefined,
): Array<Step> => {
    const steps = [];

    if (subGroupCount > 1) {
        steps.push(Step.VBP);
    } else if (shouldShowRentReserveStep(subGroupCount, buttonLayout)) {
        steps.push(Step.RENT_RESERVE_QUOTE);
    }
    steps.push(Step.FUNNEL);

    return steps;
};

type StepDetails = {
    title: string;
    description: string;
};

/**
 * Returns the title and description of the step for use in the Journey Header
 */
const getStepDetails = (currentStep: Step | null): StepDetails => {
    switch (currentStep) {
        case Step.RENT_RESERVE_QUOTE:
            return {
                title: "Rent or Reserve",
                description: "",
            };
        case Step.VBP:
            return {
                title: "",
                description: "",
            };
        case Step.FUNNEL:
            return {
                title: "",
                description: "",
            };
        default:
            return {
                title: "Unknown Step",
                description: "Please proceed to the next step.",
            };
    }
};

export const useSteps = () => {
    const currentStep = useSelector(selectCurrentStep);
    const { unitGroup } = useUnitItemContext();
    const { buttonLayout } = useDisplayOptionContext();

    const subGroupCount = unitGroup.sub_groups?.length ?? 0;

    const steps = getSteps(subGroupCount, buttonLayout);

    const { title, description } = getStepDetails(currentStep);

    return {
        steps,
        stepTitle: title,
        stepDescription: description,
    };
};
